import { useEffect, useState } from 'react';
import { useAppDispatch } from '../../hooks/StoreHooks';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ServiceModuleTiles from './ServiceModuleDnD';
import { ServiceModulesWrapper, StyledAddButton, StyledContainerHeader } from './ServiceModules.styles';
import { deleteServiceModuleThunk, fetchServiceModulesThunk } from '../../store/thunk/serviceModulesThunk';
import ServiceModuleCreator from './ServiceModuleCreator';
import { resetNewestServiceModule } from '../../store/reducers/serviceModulesReducer';
import { ServiceModule, ServiceModuleTypes } from '../../types/serviceModuleTypes';
import { useNewestServiceModule } from '../../store/selectors/serviceModulesSelector';
import FolderModuleEditor from './ServiceModuleEditor';
import { Box, ClickAwayListener, Popper, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from "@mui/icons-material/Add";
import { PermissionEntityType } from '../../types/permissionType';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';

export const defaultIcon = (type: string): string => {
    switch (type) {
        case ServiceModuleTypes.Calendar:
            return 'calendar month';
        case ServiceModuleTypes.Folder:
            return 'folder';
        case ServiceModuleTypes.File:
            return 'file';
        case ServiceModuleTypes.Text:
            return 'text';
        case ServiceModuleTypes.Url:
            return 'url';
        case ServiceModuleTypes.Leaderboard:
            return 'leaderboard';
        case ServiceModuleTypes.Timetracking:
            return 'timer';
        default:
            return 'circle';
    }
};

const ServiceModulesContainer = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [editingModule, setEditingModule] = useState<ServiceModule | null>(null);
    const newestServiceModule = useNewestServiceModule();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const { userHasPermission } = usePermissionChecker();
    const canCreateServiceModule = userHasPermission(PermissionEntityType.ServiceModules, "Create");

    const handleTogglePopper = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl((prev) => (prev ? null : event.currentTarget))
    }

    const handleClosePopper = () => setAnchorEl(null)

    useEffect(() => {
        dispatch(fetchServiceModulesThunk());
    }, [dispatch]);

    useEffect(() => {
        if (newestServiceModule) {
            setEditingModule(newestServiceModule);
            dispatch(resetNewestServiceModule());
        }
    }, [newestServiceModule, dispatch]);

    const handleCloseModal = () => {
        setEditingModule(null);
    };

    const handleDelete = async (id: number) => {
        const confirmDelete = window.confirm(t("serviceModulesPage.folderServicePage.deleteConfirmation"));
        if (confirmDelete) {
            await dispatch(deleteServiceModuleThunk(id));
            setEditingModule(null);
        }
    };

    return <ServiceModulesWrapper>
        <StyledContainerHeader>
            <Typography variant="h4" mb={2}>
                {t("serviceModulesPage.serviceModules")}
            </Typography>

            {canCreateServiceModule && (
                <Box mb={2}>
                    <Tooltip title={t("buttons.addItem")} arrow>
                        <StyledAddButton
                            variant="outlined"
                            onClick={handleTogglePopper}
                            startIcon={<AddIcon />}
                            aria-label={t("buttons.addItem")}
                        />
                    </Tooltip>
                </Box>
            )}

            <Popper open={open} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 1300 }}>
                <ClickAwayListener onClickAway={handleClosePopper}>
                    <Box>
                        <ServiceModuleCreator onClose={handleClosePopper} />
                    </Box>
                </ClickAwayListener>
            </Popper>
        </StyledContainerHeader>
        <DndProvider backend={HTML5Backend}>
            <ServiceModuleTiles />
        </DndProvider>

        {editingModule && (
            <FolderModuleEditor
                module={editingModule}
                onClose={handleCloseModal}
                onDelete={handleDelete}
            />
        )}
    </ServiceModulesWrapper>;
};

export default ServiceModulesContainer;
